import CornerBg from "@/assets/img/illustrations/corner-2.png";
import { useGetInstructorQuery } from "@/services/instructors";
import classNames from "classnames";
import { ElementRef, FC, useEffect, useRef } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";

import Background from "@/components/atoms/Background";
import EmailLink from "@/components/atoms/EmailLink";
import ErrorComponent from "@/components/atoms/ErrorComponent";
import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import Loader from "@/components/atoms/Loader";
import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import SoftBadge from "@/components/atoms/SoftBadge";
import UnknownBadge from "@/components/atoms/UnknownBadge";
import { getTvaOrNot } from "@/helpers/UserHelpers";
import { useClickToCall } from "@/hooks/useClickToCall";
import { CardDetailsProps } from "@/interfaces/Card";
import Instructor from "@/interfaces/Instructor";
import { faEye, faPhone, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import CardHeader from "../../card-headers/CardHeader";
import StoreInstructorNoteModal from "../../modals/instructors/StoreInstructorNoteModal";
import InstructorCourses from "./InstructorCourses";

const InstructorDetailsCard: FC<CardDetailsProps<Instructor>> = ({
  id,
  card,
  showDetailsButton,
  callBack,
}) => {
  const StoreInstructorModalRef =
    useRef<ElementRef<typeof StoreInstructorNoteModal>>(null);

  const { data, isLoading, isError, error } = useGetInstructorQuery(id);
  const onCall = useClickToCall();

  useEffect(() => {
    data && callBack?.(data);
  }, [data]);

  const onTriggerModal = () =>
    StoreInstructorModalRef.current?.showOrHiddenModal();

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <div
      className={classNames({
        card: card,
      })}
    >
      <StoreInstructorNoteModal ref={StoreInstructorModalRef} id={id} />
      {card && <CardHeader title="Informations du formateur" />}
      <div
        className={classNames("bg-light", {
          "card-body": card,
        })}
      >
        <Card className="border rounded-3 bg-white dark__bg-1000 shadow-none">
          <Background
            image={CornerBg}
            className="bg-card d-none d-sm-block d-xl-none"
          />
          <Card.Body className="row g-0 flex-column flex-sm-row flex-xl-column z-index-1 align-items-center">
            <Col sm={8} md={6} lg={4} xl={12} className="ps-sm-1 ps-xl-0">
              <Flex alignItems="xl-center" direction="column">
                <p className="fw-semi-bold text-800 mb-0">{data?.fullname}</p>
              </Flex>
              <Flex justifyContent="center" className="gap-2 mt-2">
                {showDetailsButton ? (
                  <IconButton
                    variant="primary"
                    icon={faEye}
                    iconAlign="left"
                    href={`/instructors/${data!.id}`}
                    transform="shrink-3 down-1"
                    size="sm"
                    className="w-50 px-2 text-nowrap"
                  >
                    <span className="fs--2">Détails</span>
                  </IconButton>
                ) : (
                  <IconButton
                    variant="primary"
                    icon={faPlus}
                    iconAlign="left"
                    onClick={onTriggerModal}
                    transform="shrink-3 down-1"
                    size="sm"
                    className="w-50 px-2 text-nowrap"
                  >
                    <span className="fs--2">Note</span>
                  </IconButton>
                )}
                {data?.phone && (
                  <IconButton
                    variant="nolina-default"
                    icon={faPhone}
                    iconAlign="left"
                    transform="shrink-4"
                    size="sm"
                    onClick={() => onCall(data.phone!)}
                    className="w-50"
                  >
                    <span className="fs--2">{data.phone}</span>
                  </IconButton>
                )}
              </Flex>
            </Col>
          </Card.Body>
        </Card>
        <Row className="border rounded-3 p-x1 mt-3 bg-white dark__bg-1000 mx-0 g-0">
          <Col md={6} xl={12} className="pe-md-4 pe-xl-0">
            <div className="mb-2">
              <Form.Label>#</Form.Label>
              {data?.id ? (
                <p className="mb-0 text-700 fs--1">{data?.id}</p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Prénom</Form.Label>
              {data?.firstname ? (
                <p className="mb-0 text-700 fs--1">{data?.firstname}</p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Nom</Form.Label>
              <br />
              {data?.lastname ? (
                <p className="mb-0 text-700 fs--1">{data?.lastname}</p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Email</Form.Label>
              <br />
              {data?.email ? (
                <EmailLink email={data.email} />
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Numéro de téléphone</Form.Label>
              <br />
              {data?.phone ? (
                <PhoneCallLink number={data.phone} />
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Adresse</Form.Label>
              <br />
              {data?.address ? (
                <p className="mb-0 text-700 fs--1">{data?.address}</p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Prix hors taxe (de base)</Form.Label>
              <br />
              {data?.priceHt ? (
                <p className="mb-0 text-700 fs--1">{data?.priceHt}</p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>NDA</Form.Label>
              <br />
              {data?.nda ? (
                <p className="mb-0 text-700 fs--1">{data?.nda}</p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Siret</Form.Label>
              <br />
              {data?.siret ? (
                <p className="mb-0 text-700 fs--1">{data?.siret}</p>
              ) : (
                <UnknownBadge />
              )}
            </div>

            <div className="mb-2">
              <Form.Label>Région d'emploi</Form.Label>
              <br />
              {data?.areas.length ? (
                data?.areas.map((area) => (
                  <p
                    key={area}
                    className="badge border link-secondary text-decoration-none me-1 mb-0"
                  >
                    {area}
                  </p>
                ))
              ) : (
                <p className="badge border link-secondary text-decoration-none me-1 mb-0">
                  Pas de données
                </p>
              )}
            </div>
            <div className="mb-2">
              <Form.Label>TVA</Form.Label>
              <br />
              <SoftBadge bg={data?.withTva ? "success" : "danger"}>
                <h6 className="mb-0 text-700 fs--1">
                  {getTvaOrNot(data?.withTva ?? false)}{" "}
                </h6>
              </SoftBadge>
            </div>
            <div className="mb-2">
              {data && <InstructorCourses instructor={data} compact />}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default InstructorDetailsCard;
